import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
} from '@mui/material';
import {
  AddRounded,
  EditNoteRounded,
  DeleteOutlineRounded,
  VisibilityRounded,
  VisibilityOffRounded,
  DragIndicatorRounded,
  LinkRounded,
  PlaylistAddRounded,
  PostAddRounded,
  AttachFileRounded,
  ExpandMoreRounded,
  ExpandLessRounded,
  OndemandVideoRounded,
  DescriptionRounded,
  MenuBookRounded,
  ArticleRounded,
  SearchRounded,
  UploadFileRounded,
  EditRounded,
  EditCalendarRounded,
  CheckRounded,
  CloseRounded,
  DriveFileMoveOutlined,
  AssignmentTurnedInRounded,
  QuizRounded,
  FactCheckRounded,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

/** YYYY-MM-DD → dd-mm-yyyy, the date format used across this admin surface. */
function fmtDayEs(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}-${m}-${y}` : iso;
}

import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../stores/tenant.store';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { cursoTabs, resolveCursoTab, type CursoTab } from './curso-tabs';
import { TenantDateTimeField } from '../../components/TenantDateTimeField';
import { TenantDateField } from '../../components/TenantDateField';
import {
  lmsApi,
  type SectionStructureDto,
  type StructureItemDto,
  type StructureModuleDto,
} from '../../lib/api/lms.api';
import { courseMaterialApi, type MaterialType } from '../../lib/api/course-material.api';
import { assignmentApi } from '../../lib/api/assignment.api';
import { attendanceApi } from '../../lib/api/attendance.api';
import { courseTemplateApi } from '../../lib/api/course-template.api';
import { PageEditor } from './components/PageEditor';
import { SectionPicker } from './components/SectionPicker';
import { CourseSummary } from './components/CourseSummary';
import { SyllabusAdminTab } from './components/SyllabusAdminTab';
import { MaterialsAdminPage } from './materials-admin.page';
import { AssignmentsAdminPage } from './assignments-admin.page';
import { GradesPage } from './grades.page';
import {
  UploadMaterialDialog,
  LinkMaterialDialog,
  BulkLinkMaterialDialog,
} from './components/material-dialogs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { errorText } from '../../lib/error-text';
import { usePermissions } from '../../hooks/use-permissions';
import { formatDate } from '../../lib/i18n-format';
import { AttendanceRosterDialog, AttendanceMatrixDialog } from './components/AttendanceDialogs';
import { AttendanceTab } from './components/AttendanceTab';

const ITEM_ICON: Record<string, JSX.Element> = {
  page: <ArticleRounded fontSize="small" sx={{ color: '#8b5cf6' }} />,
  'material:video': <OndemandVideoRounded fontSize="small" sx={{ color: '#ef4444' }} />,
  'material:notes': <DescriptionRounded fontSize="small" sx={{ color: '#3b82f6' }} />,
  'material:reading': <MenuBookRounded fontSize="small" sx={{ color: '#10b981' }} />,
};

function itemIcon(it: StructureItemDto): JSX.Element {
  if (it.type === 'page') return ITEM_ICON['page']!;
  if (it.type === 'assignment') {
    return it.assignment?.isQuiz ? (
      <QuizRounded fontSize="small" sx={{ color: '#f59e0b' }} />
    ) : (
      <AssignmentTurnedInRounded fontSize="small" sx={{ color: '#f59e0b' }} />
    );
  }
  const key = `material:${it.material?.materialType ?? 'notes'}`;
  return ITEM_ICON[key] ?? ITEM_ICON['material:notes']!;
}

export function LmsAdminPage(): JSX.Element {
  const { t } = useTranslation(['sis', 'common']);
  void t;
  const { currentTenantId } = useTenantStore();

  // Section + active tab live in the URL so the cockpit is deep-linkable and
  // survives refresh: /curso/:sectionId?tab=modules|cronograma|biblioteca
  const navigate = useNavigate();
  const { sectionId: urlSectionId } = useParams<{ sectionId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSectionId = urlSectionId ?? '';
  const goToSection = (id: string) => {
    const tab = searchParams.get('tab');
    const suffix = tab ? `?tab=${tab}` : '';
    navigate(id ? `/curso/${id}${suffix}` : '/curso');
  };

  const [structure, setStructure] = useState<SectionStructureDto | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newModuleOpen, setNewModuleOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<StructureModuleDto | null>(null);
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null);
  const [deletingModuleBusy, setDeletingModuleBusy] = useState(false);
  const [pageEditorState, setPageEditorState] = useState<{
    mode: 'create' | 'edit';
    moduleId: string;
    pageId?: string;
  } | null>(null);
  const [addItemAnchor, setAddItemAnchor] = useState<{
    moduleId: string;
    el: HTMLElement;
  } | null>(null);
  const [materialPickerModuleId, setMaterialPickerModuleId] = useState<string | null>(null);
  const [assignmentPickerModuleId, setAssignmentPickerModuleId] = useState<string | null>(null);
  const [inlineMaterialState, setInlineMaterialState] = useState<{
    moduleId: string;
    tab: 'upload' | 'link' | 'bulk';
  } | null>(null);
  const [editingMaterialItem, setEditingMaterialItem] = useState<StructureItemDto | null>(null);
  // `graduation_audit`: sin libro de notas ni rendimiento numérico (ver curso-tabs).
  const importedRecord = useTenantConfig().academicRecord.imported;
  // Notas sólo a quien el servidor le da el libro (curso-tabs).
  const { roles: myRoles } = usePermissions();
  const offeredTabs = cursoTabs(myRoles);
  // EL ATERRIZAJE DEL CURSO. Antes caía en Módulos, que es donde se EDITA;
  // para saber cómo va el curso había que abrir tres pestañas y comparar a
  // ojo. Los enlaces con `?tab=` siguen funcionando igual.
  const activeTab: CursoTab = resolveCursoTab(searchParams.get('tab'), offeredTabs);
  const setActiveTab = (v: CursoTab) => {
    setSearchParams(
      (prev) => {
        prev.set('tab', v);
        return prev;
      },
      { replace: true },
    );
  };

  const reloadStructure = async () => {
    if (!currentTenantId || !selectedSectionId) {
      setStructure(null);
      return;
    }
    setLoadingStructure(true);
    setError(null);
    try {
      const res = await lmsApi.getSectionStructure({
        tenantId: currentTenantId,
        sectionId: selectedSectionId,
      });
      setStructure(res);
    } catch (e: unknown) {
      setError(errorText(e, 'Error al cargar la estructura'));
    } finally {
      setLoadingStructure(false);
    }
  };

  useEffect(() => {
    void reloadStructure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId, selectedSectionId]);

  // Materials already placed in ANY module (a material may live in several modules).
  // The Biblioteca tab shows only the UNASSIGNED pool, so these are hidden there.
  const assignedMaterialIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of structure?.modules ?? []) {
      for (const it of m.items) if (it.type === 'material') ids.add(it.targetId);
    }
    return ids;
  }, [structure]);

  const handleAssignMaterialToModule = async (
    materialId: string,
    moduleId: string,
  ): Promise<void> => {
    if (!currentTenantId) return;
    try {
      await lmsApi.createModuleItem({
        tenantId: currentTenantId,
        sectionId: selectedSectionId,
        moduleId,
        type: 'material',
        targetId: materialId,
      });
      await reloadStructure();
    } catch (e: unknown) {
      setError(errorText(e, 'Error al agregar el material al módulo'));
    }
  };

  // ── DnD setup ────────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleItemDragEnd = async (moduleId: string, event: DragEndEvent) => {
    if (!structure || !currentTenantId) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const mod = structure.modules.find((m) => m.id === moduleId);
    if (!mod) return;
    const oldIdx = mod.items.findIndex((i) => i.id === active.id);
    const newIdx = mod.items.findIndex((i) => i.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(mod.items, oldIdx, newIdx);
    setStructure({
      ...structure,
      modules: structure.modules.map((m) => (m.id === moduleId ? { ...m, items: reordered } : m)),
    });
    try {
      await lmsApi.reorderModuleItems({
        tenantId: currentTenantId,
        moduleId,
        orderedIds: reordered.map((i) => i.id),
      });
    } catch (e: unknown) {
      setError(errorText(e, 'Error al reordenar items'));
      void reloadStructure();
    }
  };

  // ── Module actions ───────────────────────────────────────────────────────
  const handlePublishToggle = async (m: StructureModuleDto, publish: boolean) => {
    if (!currentTenantId) return;
    try {
      await lmsApi.publishModule({
        tenantId: currentTenantId,
        moduleId: m.id,
        publish,
      });
      await reloadStructure();
    } catch (e: unknown) {
      setError(errorText(e));
    }
  };

  const handleConfirmDeleteModule = async () => {
    if (!currentTenantId || !deletingModuleId) return;
    setDeletingModuleBusy(true);
    try {
      await lmsApi.deleteModule({ tenantId: currentTenantId, moduleId: deletingModuleId });
      setDeletingModuleId(null);
      await reloadStructure();
    } catch (e: unknown) {
      setError(errorText(e, 'Error al eliminar'));
    } finally {
      setDeletingModuleBusy(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!currentTenantId) return;
    try {
      await lmsApi.deleteModuleItem({ tenantId: currentTenantId, itemId });
      await reloadStructure();
    } catch (e: unknown) {
      setError(errorText(e));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('lms.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('lms.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <SectionPicker value={selectedSectionId} onChange={goToSection} />
          {/* "Nuevo módulo" belongs to the Módulos/Cronograma tabs; Biblioteca
              and Asignaciones have their own actions. */}
          {(activeTab === 'modules' || activeTab === 'cronograma') && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              disabled={!selectedSectionId}
              onClick={() => setNewModuleOpen(true)}
            >
              {t('lms.newModule')}
            </Button>
          )}
        </Stack>
      </Paper>

      {!selectedSectionId && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">{t('lms.selectSection')}</Typography>
        </Paper>
      )}

      {selectedSectionId && loadingStructure && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Cargando estructura…</Typography>
        </Stack>
      )}

      {selectedSectionId && !loadingStructure && structure && (
        <Stack spacing={2}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontSize: 13 },
            }}
          >
            <Tab value="resumen" label={t('lms.tabs.summary', { defaultValue: 'Resumen' })} />
            <Tab value="modules" label={t('lms.tabs.modules', { defaultValue: 'Módulos' })} />
            <Tab value="syllabus" label={t('lms.tabs.syllabus', { defaultValue: 'Syllabus' })} />
            <Tab
              value="cronograma"
              label={t('lms.tabs.cronograma', { defaultValue: 'Cronograma' })}
            />
            <Tab
              value="asistencia"
              label={t('lms.tabs.attendance', { defaultValue: 'Asistencia' })}
            />
            <Tab value="biblioteca" label={t('lms.tabs.library', { defaultValue: 'Biblioteca' })} />
            <Tab
              value="asignaciones"
              label={t('lms.tabs.assignments', { defaultValue: 'Tareas' })}
            />
            {offeredTabs.includes('notas') && (
              <Tab value="notas" label={t('lms.tabs.grades', { defaultValue: 'Notas' })} />
            )}
          </Tabs>
        </Stack>
      )}
      {/* EXIGE `structure`, como las demás pestañas. Sin ella «no tiene ninguna
          tarea publicada» sería una alarma FABRICADA por una lectura que
          falló — y es justo la alarma que esta pantalla existe para levantar,
          así que es la que menos puede mentir. */}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'resumen' && (
        <Box sx={{ mt: 2 }}>
          <CourseSummary
            sectionId={selectedSectionId}
            structure={structure}
            onGoToTab={setActiveTab}
            importedRecord={importedRecord}
          />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'syllabus' && (
        <Box sx={{ mt: 2 }}>
          <SyllabusAdminTab sectionId={selectedSectionId} onStructureChanged={reloadStructure} />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'asignaciones' && (
        <Box sx={{ mt: 2 }}>
          <AssignmentsAdminPage
            embeddedSectionId={selectedSectionId}
            moduleOptions={structure.modules.map((m) => ({
              id: m.id,
              title: m.title,
              sortOrder: m.sortOrder,
            }))}
            placementsByAssignment={structure.modules.reduce(
              (acc, m) => {
                for (const it of m.items) {
                  if (it.type !== 'assignment') continue;
                  (acc[it.targetId] ??= []).push({ moduleId: m.id, itemId: it.id });
                }
                return acc;
              },
              {} as Record<string, { moduleId: string; itemId: string }[]>,
            )}
            onStructureChanged={reloadStructure}
          />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'notas' && (
        <Box sx={{ mt: 2 }}>
          <GradesPage embeddedSectionId={selectedSectionId} />
        </Box>
      )}

      {selectedSectionId && activeTab === 'asistencia' && (
        <Box sx={{ mt: 2 }}>
          <AttendanceTab sectionId={selectedSectionId} />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'biblioteca' && (
        <Box sx={{ mt: 2 }}>
          <MaterialsAdminPage
            embeddedSectionId={selectedSectionId}
            hideMaterialIds={[...assignedMaterialIds]}
            moduleOptions={structure.modules.map((m) => ({ id: m.id, title: m.title }))}
            onAssignToModule={handleAssignMaterialToModule}
          />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'cronograma' && (
        <Box sx={{ mt: 2 }}>
          <CronogramaView
            tenantId={currentTenantId ?? ''}
            sectionId={selectedSectionId}
            modules={structure.modules.map((m) => ({ id: m.id, title: m.title }))}
            onEditModule={(id) => {
              const mod = structure.modules.find((x) => x.id === id);
              if (mod) setEditingModule(mod);
            }}
          />
        </Box>
      )}
      {selectedSectionId && !loadingStructure && structure && activeTab === 'modules' && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {/* No drag here on purpose: modules are ordered by their first class
              date (server-side), so a hand-dragged order had no lasting meaning
              — the next syllabus re-generation silently overwrote sortOrder.
              Items INSIDE a module keep their drag: that order is editorial. */}
          {structure.modules.map((m) => (
            <SortableModule
              key={m.id}
              module={m}
              onEdit={() => setEditingModule(m)}
              onDelete={() => setDeletingModuleId(m.id)}
              onPublishToggle={(pub) => handlePublishToggle(m, pub)}
              onAddItem={(el) => setAddItemAnchor({ moduleId: m.id, el })}
              onItemDragEnd={(e) => handleItemDragEnd(m.id, e)}
              onDeleteItem={(itemId) => handleDeleteItem(itemId)}
              onOpenPage={(itemId, pageId) => {
                setPageEditorState({ mode: 'edit', moduleId: m.id, pageId });
                void itemId;
              }}
              onEditItem={(item) => {
                if (item.type === 'page') {
                  setPageEditorState({
                    mode: 'edit',
                    moduleId: m.id,
                    pageId: item.targetId,
                  });
                } else {
                  setEditingMaterialItem(item);
                }
              }}
              onOpenSchedule={() => setActiveTab('cronograma')}
              sensors={sensors}
            />
          ))}

          {structure.unassignedItems.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'warning.main' }}>
              <Typography variant="subtitle2" fontWeight={700} color="warning.main">
                Sin asignar a módulo ({structure.unassignedItems.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Estos ítems no están publicados a los alumnos. Asígnalos a un módulo o elimínalos.
              </Typography>
              <Stack spacing={0.5}>
                {structure.unassignedItems.map((it) => (
                  <Stack
                    key={it.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ px: 1, py: 0.5 }}
                  >
                    {itemIcon(it)}
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {it.title}
                    </Typography>
                    <Tooltip title={it.type === 'page' ? 'Editar página' : 'Editar material'}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (it.type === 'page') {
                            setPageEditorState({
                              mode: 'edit',
                              moduleId: it.moduleId,
                              pageId: it.targetId,
                            });
                          } else {
                            setEditingMaterialItem(it);
                          }
                        }}
                      >
                        <EditNoteRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(it.id)}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          {structure.modules.length === 0 && structure.unassignedItems.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color="text.secondary">
                Aún no hay módulos. Crea el primero con el botón "Nuevo módulo".
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* Dialogs */}
      <ModuleDialog
        open={newModuleOpen}
        onClose={() => setNewModuleOpen(false)}
        onSuccess={async () => {
          setNewModuleOpen(false);
          await reloadStructure();
        }}
        sectionId={selectedSectionId}
      />
      <ModuleDialog
        open={!!editingModule}
        module={editingModule}
        onClose={() => setEditingModule(null)}
        onSuccess={async () => {
          setEditingModule(null);
          await reloadStructure();
        }}
        sectionId={selectedSectionId}
      />
      <Dialog open={!!deletingModuleId} onClose={() => setDeletingModuleId(null)}>
        <DialogTitle>Eliminar módulo</DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción elimina el módulo y todos sus ítems (las páginas y materiales originales no
            se eliminan, solo se desvinculan).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingModuleId(null)} disabled={deletingModuleBusy}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteModule}
            disabled={deletingModuleBusy}
            startIcon={deletingModuleBusy ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deletingModuleBusy ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {pageEditorState && (
        <PageEditorDialog
          mode={pageEditorState.mode}
          sectionId={selectedSectionId}
          moduleId={pageEditorState.moduleId}
          {...(pageEditorState.pageId ? { pageId: pageEditorState.pageId } : {})}
          onClose={() => setPageEditorState(null)}
          onSuccess={async () => {
            setPageEditorState(null);
            await reloadStructure();
          }}
        />
      )}

      {editingMaterialItem && (
        <MaterialEditDialog
          item={editingMaterialItem}
          onClose={() => setEditingMaterialItem(null)}
          onSuccess={async () => {
            setEditingMaterialItem(null);
            await reloadStructure();
          }}
        />
      )}

      {/* Add item menu */}
      <Menu
        open={!!addItemAnchor}
        anchorEl={addItemAnchor?.el}
        onClose={() => setAddItemAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setPageEditorState({ mode: 'create', moduleId: addItemAnchor.moduleId });
            setAddItemAnchor(null);
          }}
        >
          <PostAddRounded fontSize="small" sx={{ mr: 1 }} /> Nueva página
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setInlineMaterialState({ moduleId: addItemAnchor.moduleId, tab: 'upload' });
            setAddItemAnchor(null);
          }}
        >
          <UploadFileRounded fontSize="small" sx={{ mr: 1 }} /> Subir archivo
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setInlineMaterialState({ moduleId: addItemAnchor.moduleId, tab: 'link' });
            setAddItemAnchor(null);
          }}
        >
          <LinkRounded fontSize="small" sx={{ mr: 1 }} /> Agregar enlace
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setInlineMaterialState({ moduleId: addItemAnchor.moduleId, tab: 'bulk' });
            setAddItemAnchor(null);
          }}
        >
          <PlaylistAddRounded fontSize="small" sx={{ mr: 1 }} /> Importar varios enlaces
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setMaterialPickerModuleId(addItemAnchor.moduleId);
            setAddItemAnchor(null);
          }}
        >
          <AttachFileRounded fontSize="small" sx={{ mr: 1 }} /> Buscar en catálogo
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!addItemAnchor) return;
            setAssignmentPickerModuleId(addItemAnchor.moduleId);
            setAddItemAnchor(null);
          }}
        >
          <AssignmentTurnedInRounded fontSize="small" sx={{ mr: 1 }} />{' '}
          {t('lms.addAssignmentItem', { defaultValue: 'Agregar tarea' })}
        </MenuItem>
      </Menu>

      {materialPickerModuleId && (
        <MaterialPickerDialog
          sectionId={selectedSectionId}
          moduleId={materialPickerModuleId}
          // A material can live in several modules; only exclude the ones already
          // in THIS module so it can be reused elsewhere.
          existingTargetIds={
            new Set(
              (structure?.modules ?? [])
                .filter((mm) => mm.id === materialPickerModuleId)
                .flatMap((mm) => mm.items)
                .filter((it) => it.type === 'material')
                .map((it) => it.targetId),
            )
          }
          onClose={() => setMaterialPickerModuleId(null)}
          onSuccess={async () => {
            setMaterialPickerModuleId(null);
            await reloadStructure();
          }}
        />
      )}

      {assignmentPickerModuleId && (
        <AssignmentPickerDialog
          sectionId={selectedSectionId}
          moduleId={assignmentPickerModuleId}
          existingTargetIds={
            new Set(
              (structure?.modules ?? [])
                .filter((mm) => mm.id === assignmentPickerModuleId)
                .flatMap((mm) => mm.items)
                .filter((it) => it.type === 'assignment')
                .map((it) => it.targetId),
            )
          }
          onClose={() => setAssignmentPickerModuleId(null)}
          onSuccess={async () => {
            setAssignmentPickerModuleId(null);
            await reloadStructure();
          }}
        />
      )}

      {inlineMaterialState &&
        (() => {
          const moduleId = inlineMaterialState.moduleId;
          const handleLinkAndReload = async (createdIds: string[]) => {
            if (!currentTenantId) return;
            try {
              for (const id of createdIds) {
                await lmsApi.createModuleItem({
                  tenantId: currentTenantId,
                  sectionId: selectedSectionId,
                  moduleId,
                  type: 'material',
                  targetId: id,
                });
              }
              setInlineMaterialState(null);
              await reloadStructure();
            } catch (e: unknown) {
              setError(errorText(e, 'Error al vincular material'));
            }
          };
          const commonProps = {
            open: true,
            sectionId: selectedSectionId,
            onClose: () => setInlineMaterialState(null),
            onSuccess: handleLinkAndReload,
          };
          if (inlineMaterialState.tab === 'upload')
            return <UploadMaterialDialog {...commonProps} />;
          if (inlineMaterialState.tab === 'link') return <LinkMaterialDialog {...commonProps} />;
          return <BulkLinkMaterialDialog {...commonProps} />;
        })()}
    </Box>
  );
}

// ─── Sortable Module ─────────────────────────────────────────────────────────

interface SortableModuleProps {
  module: StructureModuleDto;
  onEdit: () => void;
  onDelete: () => void;
  onPublishToggle: (publish: boolean) => void;
  onAddItem: (el: HTMLElement) => void;
  onItemDragEnd: (event: DragEndEvent) => void;
  onDeleteItem: (itemId: string) => void;
  onOpenPage: (itemId: string, pageId: string) => void;
  onEditItem: (item: StructureItemDto) => void;
  onOpenSchedule: () => void;
  sensors: ReturnType<typeof useSensors>;
}

function SortableModule({
  module,
  onEdit,
  onDelete,
  onPublishToggle,
  onAddItem,
  onItemDragEnd,
  onDeleteItem,
  onOpenPage,
  onEditItem,
  onOpenSchedule,
  sensors,
}: SortableModuleProps): JSX.Element {
  const { t } = useTranslation('sis');
  const [expanded, setExpanded] = useState(true);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 1,
          bgcolor: 'action.hover',
        }}
      >
        <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {module.title}
            </Typography>
            {!module.publishedAt && (
              <Chip
                label={t('lms.moduleChip.draft', { defaultValue: 'Borrador' })}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
            {module.releaseAt && (
              <Chip
                label={t('lms.moduleChip.visibleFrom', {
                  date: module.releaseAt.substring(0, 10),
                  defaultValue: `Visible desde ${module.releaseAt.substring(0, 10)}`,
                })}
                size="small"
                variant="outlined"
                sx={{ height: 20 }}
              />
            )}
            {/* Provenance: materialized from the syllabus template vs created by hand.
                The sortOrder-0 "Información del curso" module is neither — no chip. */}
            {module.templateModuleId ? (
              <Chip
                label={t('lms.moduleChip.fromSyllabus', { defaultValue: 'Del syllabus' })}
                size="small"
                variant="outlined"
                color="info"
                sx={{ height: 20 }}
              />
            ) : module.sortOrder > 0 ? (
              <Chip
                label={t('lms.moduleChip.manual', { defaultValue: 'Manual' })}
                size="small"
                variant="outlined"
                sx={{ height: 20 }}
              />
            ) : null}
          </Stack>
          {module.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {module.description}
            </Typography>
          )}
          {/* When it is taught. A module IS a class block in this domain, so
              showing its dates here is what makes the Módulos tab and the
              Cronograma read as the same thing. */}
          {module.firstSessionDate && (
            <Typography variant="caption" color="text.secondary" display="block">
              {t('lms.moduleChip.taughtOn', {
                range:
                  module.lastSessionDate && module.lastSessionDate !== module.firstSessionDate
                    ? `${fmtDayEs(module.firstSessionDate)} – ${fmtDayEs(module.lastSessionDate)}`
                    : fmtDayEs(module.firstSessionDate),
                defaultValue: 'Se dicta: {{range}}',
              })}
            </Typography>
          )}
        </Box>
        <Tooltip title={module.publishedAt ? 'Despublicar' : 'Publicar'}>
          <IconButton size="small" onClick={() => onPublishToggle(!module.publishedAt)}>
            {module.publishedAt ? (
              <VisibilityRounded fontSize="small" color="primary" />
            ) : (
              <VisibilityOffRounded fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar módulo">
          <IconButton size="small" onClick={onEdit}>
            <EditNoteRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar módulo">
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteOutlineRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ver los días de clase de este módulo en el Cronograma">
          <Button
            size="small"
            onClick={onOpenSchedule}
            sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0 }}
          >
            Cronograma →
          </Button>
        </Tooltip>
        <Button size="small" startIcon={<AddRounded />} onClick={(e) => onAddItem(e.currentTarget)}>
          Item
        </Button>
      </Box>

      {expanded && (
        <Box sx={{ p: 1 }}>
          {module.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              Módulo vacío — agrega una página o vincula un material.
            </Typography>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onItemDragEnd}
            >
              <SortableContext
                items={module.items.map((i) => i.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack spacing={0.5}>
                  {module.items.map((it) => (
                    <SortableItem
                      key={it.id}
                      item={it}
                      onDelete={() => onDeleteItem(it.id)}
                      onOpenPage={() => {
                        if (it.type === 'page') onOpenPage(it.id, it.targetId);
                      }}
                      onEdit={() => onEditItem(it)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Box>
      )}
    </Paper>
  );
}

function SortableItem({
  item,
  onDelete,
  onOpenPage,
  onEdit,
}: {
  item: StructureItemDto;
  onDelete: () => void;
  onOpenPage: () => void;
  onEdit: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    paddingLeft: 8 + item.indent * 24,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        bgcolor: 'background.paper',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 1,
      }}
    >
      <IconButton size="small" {...attributes} {...listeners} sx={{ cursor: 'grab' }}>
        <DragIndicatorRounded fontSize="small" />
      </IconButton>
      {itemIcon(item)}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: item.type === 'page' ? 'pointer' : 'default',
        }}
        onClick={item.type === 'page' ? onOpenPage : undefined}
      >
        {item.title}
      </Typography>
      <Tooltip title={item.type === 'page' ? 'Editar página' : 'Editar material'}>
        <IconButton size="small" onClick={onEdit}>
          <EditNoteRounded fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Quitar del módulo">
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteOutlineRounded fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ─── Material edit dialog ────────────────────────────────────────────────────
// Edits a material item's title + description. Updates both the underlying
// CourseMaterial record (canonical) and the LmsModuleItem.title (per-module
// override) so the change is visible everywhere consistently.

function MaterialEditDialog({
  item,
  onClose,
  onSuccess,
}: {
  item: StructureItemDto;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [title, setTitle] = useState(item.title ?? '');
  const [description, setDescription] = useState(item.material?.description ?? '');
  const [type, setType] = useState<MaterialType>(item.material?.materialType ?? 'notes');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // A file-backed material cannot become a `video` (videos must be URLs).
  const isFile = !!item.material?.storagePath;
  const typeOptions: MaterialType[] = isFile ? ['notes', 'reading'] : ['video', 'notes', 'reading'];

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setErr(t('courseMaterials.editDialog.titleRequired'));
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await courseMaterialApi.update({
        tenantId: currentTenantId,
        materialId: item.targetId,
        title: trimmedTitle,
        description: description.trim(),
        type,
      });
      await lmsApi.updateModuleItem({
        tenantId: currentTenantId,
        itemId: item.id,
        title: trimmedTitle,
      });
      await onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const typeLabel = t(`courseMaterials.types.${item.material?.materialType ?? 'notes'}`);

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('courseMaterials.editDialog.titleWithType', { type: typeLabel })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label={t('courseMaterials.editDialog.fieldTitle')}
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
            autoFocus
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="material-type-label">
              {t('courseMaterials.editDialog.fieldType')}
            </InputLabel>
            <Select
              labelId="material-type-label"
              label={t('courseMaterials.editDialog.fieldType')}
              value={type}
              onChange={(e) => setType(e.target.value as MaterialType)}
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {t(`courseMaterials.types.${opt}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('courseMaterials.editDialog.fieldDescription')}
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            inputProps={{ maxLength: 2000 }}
          />
          {item.material?.url && (
            <Typography variant="caption" color="text.secondary">
              {t('courseMaterials.editDialog.currentLink', { url: item.material.url })}
            </Typography>
          )}
          {item.material?.originalFileName && (
            <Typography variant="caption" color="text.secondary">
              {t('courseMaterials.editDialog.currentFile', {
                name: item.material.originalFileName,
              })}
            </Typography>
          )}
          {isFile && (
            <Typography variant="caption" color="text.secondary">
              {t('courseMaterials.editDialog.videoNeedsUrl')}
            </Typography>
          )}
          <Alert severity="info" sx={{ fontSize: 12 }}>
            {t('courseMaterials.editDialog.note')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('courseMaterials.editDialog.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? t('courseMaterials.editDialog.saving')
            : t('courseMaterials.editDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Module dialog (create + edit) ───────────────────────────────────────────

/** ISO string → value for <input type="datetime-local"> (local time, no tz). */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ModuleDialog({
  open,
  module,
  sectionId,
  onClose,
  onSuccess,
}: {
  open: boolean;
  module?: StructureModuleDto | null;
  sectionId: string;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [releaseAt, setReleaseAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle(module?.title ?? '');
      setDescription(module?.description ?? '');
      setReleaseAt(module?.releaseAt ? isoToLocalInput(module.releaseAt) : '');
      setErr(null);
    }
  }, [open, module]);

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!title.trim()) {
      setErr(t('lms.moduleDialog.titleRequired'));
      return;
    }
    const releaseAtIso = releaseAt ? new Date(releaseAt).toISOString() : null;
    setSubmitting(true);
    setErr(null);
    try {
      if (module) {
        await lmsApi.updateModule({
          tenantId: currentTenantId,
          moduleId: module.id,
          title: title.trim(),
          description: description.trim(),
          releaseAt: releaseAtIso,
        });
      } else {
        await lmsApi.createModule({
          tenantId: currentTenantId,
          sectionId,
          title: title.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(releaseAtIso ? { releaseAt: releaseAtIso } : {}),
        });
      }
      await onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {module ? t('lms.moduleDialog.editTitle') : t('lms.moduleDialog.newTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            label={t('lms.moduleDialog.title')}
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
          <TextField
            label={t('lms.moduleDialog.description')}
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            inputProps={{ maxLength: 2000 }}
          />
          <TenantDateTimeField
            label={t('lms.moduleDialog.releaseAt')}
            value={releaseAt}
            onChange={setReleaseAt}
            helperText={t('lms.moduleDialog.releaseHelp')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('lms.moduleDialog.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? t('lms.moduleDialog.saving') : t('lms.moduleDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Cronograma view (section-wide class calendar) ──────────────────────────
// Lists every materialized CourseSession of a section grouped by (date,
// module) so the admin can reschedule a single día without opening each
// module separately. Replaces the in-module date editor — date overrides are
// a calendar-level concern, not a module-level one.

function CronogramaView({
  tenantId,
  sectionId,
  modules,
  onEditModule,
}: {
  tenantId: string;
  sectionId: string;
  modules: { id: string; title: string }[];
  onEditModule: (moduleId: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['section-sessions', tenantId, sectionId],
    queryFn: () => courseTemplateApi.listSectionSessions({ tenantId, sectionId }),
    enabled: !!sectionId,
    staleTime: 30_000,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['section-sessions', tenantId, sectionId] });

  const updateDay = useMutation({
    mutationFn: (input: { moduleId: string; oldDate: string; newDate: string }) =>
      courseTemplateApi.updateModuleDayDate({
        tenantId,
        moduleId: input.moduleId,
        oldDate: input.oldDate,
        newDate: input.newDate,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });

  // ── per-session topic editing ──
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingDayKey, setEditingDayKey] = useState<string | null>(null);
  const [editTopicDraft, setEditTopicDraft] = useState('');
  // ── attendance (per class DAY) ──
  const [attendanceDay, setAttendanceDay] = useState<{ moduleId: string; date: string } | null>(
    null,
  );
  const [matrixOpen, setMatrixOpen] = useState(false);
  const updateModality = useMutation({
    mutationFn: (input: {
      moduleId: string;
      date: string;
      modality: 'in-person' | 'online' | 'hybrid';
    }) =>
      attendanceApi.updateDayModality({
        tenantId,
        moduleId: input.moduleId,
        date: input.date,
        modality: input.modality,
      }),
    onSuccess: () => {
      setSavedMsg('Modalidad del día actualizada.');
      void invalidate();
    },
    onError: (e: unknown) => {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al cambiar la modalidad');
    },
  });

  const editSession = useMutation({
    mutationFn: (input: { sessionId: string; topic: string }) =>
      courseTemplateApi.editSession({
        tenantId,
        sessionId: input.sessionId,
        topic: input.topic,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });

  const removeSession = useMutation({
    mutationFn: (input: { sessionId: string }) =>
      courseTemplateApi.removeSession({ tenantId, sessionId: input.sessionId }),
    onSuccess: () => {
      void invalidate();
    },
  });

  const startEditSession = (sessionId: string, topic: string) => {
    setEditingSessionId(sessionId);
    setEditTopicDraft(topic);
  };

  const cancelEditSession = () => {
    setEditingSessionId(null);
    setEditTopicDraft('');
  };

  const saveEditSession = async (sessionId: string) => {
    const topic = editTopicDraft.trim();
    if (!topic) return;
    setSavedMsg(null);
    try {
      await editSession.mutateAsync({ sessionId, topic });
      cancelEditSession();
    } catch (e) {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al editar la sesión');
    }
  };

  const handleRemoveSession = async (sessionId: string) => {
    if (!window.confirm('¿Quitar esta sesión del cronograma de la sección?')) return;
    setSavedMsg(null);
    try {
      await removeSession.mutateAsync({ sessionId });
      if (editingSessionId === sessionId) cancelEditSession();
    } catch (e) {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al quitar la sesión');
    }
  };

  // ── move-session-to-another-section dialog ──
  // Move a session to ANOTHER MODULE of the same section.
  const [movingSession, setMovingSession] = useState<{
    id: string;
    topic: string;
    moduleId: string;
  } | null>(null);
  const [targetModuleId, setTargetModuleId] = useState('');

  const openMoveDialog = (id: string, topic: string, moduleId: string) => {
    setMovingSession({ id, topic, moduleId });
    setTargetModuleId('');
  };

  const closeMoveDialog = () => {
    setMovingSession(null);
    setTargetModuleId('');
  };

  const moveSession = useMutation({
    mutationFn: (input: { sessionId: string; targetModuleId: string }) =>
      courseTemplateApi.moveSessionToModule({
        tenantId,
        sessionId: input.sessionId,
        targetModuleId: input.targetModuleId,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });

  const moveValid =
    !!targetModuleId && !!movingSession && targetModuleId !== movingSession.moduleId;

  const handleMoveSession = async () => {
    if (!movingSession || !moveValid) return;
    setSavedMsg(null);
    try {
      await moveSession.mutateAsync({ sessionId: movingSession.id, targetModuleId });
      closeMoveDialog();
      setSavedMsg('Sesión movida a otro módulo.');
    } catch (e) {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al mover la sesión');
    }
  };

  // Move a WHOLE DAY (all its sessions) to another module of the same section.
  const [movingDay, setMovingDay] = useState<{ moduleId: string; date: string } | null>(null);
  const [dayTargetModuleId, setDayTargetModuleId] = useState('');
  const openMoveDay = (moduleId: string, date: string) => {
    setMovingDay({ moduleId, date });
    setDayTargetModuleId('');
  };
  const closeMoveDay = () => {
    setMovingDay(null);
    setDayTargetModuleId('');
  };
  const moveDay = useMutation({
    mutationFn: (input: { sourceModuleId: string; date: string; targetModuleId: string }) =>
      courseTemplateApi.moveDayToModule({ tenantId, ...input }),
    onSuccess: () => {
      void invalidate();
    },
  });
  const moveDayValid =
    !!dayTargetModuleId && !!movingDay && dayTargetModuleId !== movingDay.moduleId;
  const handleMoveDay = async () => {
    if (!movingDay || !moveDayValid) return;
    setSavedMsg(null);
    try {
      const res = await moveDay.mutateAsync({
        sourceModuleId: movingDay.moduleId,
        date: movingDay.date,
        targetModuleId: dayTargetModuleId,
      });
      closeMoveDay();
      setSavedMsg(`Día movido a otro módulo (${res.moved} sesión(es)).`);
    } catch (e) {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al mover el día');
    }
  };

  // ── add-session dialog ──
  const [addDialog, setAddDialog] = useState<{ moduleId: string } | null>(null);
  const [addDate, setAddDate] = useState('');
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const [addTopic, setAddTopic] = useState('');

  // `date` seeds the picker (a day-group passes its date; a module-level add
  // passes '' so the teacher chooses one).
  const openAddDialog = (moduleId: string, date: string) => {
    setAddDialog({ moduleId });
    setAddDate(date);
    setAddStart('');
    setAddEnd('');
    setAddTopic('');
  };

  const closeAddDialog = () => setAddDialog(null);

  const addSession = useMutation({
    mutationFn: (input: {
      moduleId: string;
      date: string;
      startTime: string;
      endTime: string;
      topic: string;
    }) =>
      courseTemplateApi.addSession({
        tenantId,
        sectionId,
        moduleId: input.moduleId,
        date: input.date,
        startTime: input.startTime,
        endTime: input.endTime,
        topic: input.topic,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });

  const addSessionValid =
    /^\d{4}-\d{2}-\d{2}$/.test(addDate) &&
    !!addStart &&
    !!addEnd &&
    addStart < addEnd &&
    addTopic.trim().length > 0;

  const handleAddSession = async () => {
    if (!addDialog || !addSessionValid) return;
    setSavedMsg(null);
    try {
      await addSession.mutateAsync({
        moduleId: addDialog.moduleId,
        date: addDate,
        startTime: addStart,
        endTime: addEnd,
        topic: addTopic.trim(),
      });
      closeAddDialog();
    } catch (e) {
      setSavedMsg(e instanceof Error ? `Error: ${e.message}` : 'Error al agregar la sesión');
    }
  };

  // draft keyed by `${moduleId}::${oldDate}` so two rows on same date but
  // different modules don't share state.
  const [draftDates, setDraftDates] = useState<Record<string, string>>({});
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState<string>('__all__');

  const groups = data?.groups ?? [];

  // All modules of the section (from the LMS structure), so brand-new / empty
  // modules also appear here and can be populated with sessions. Any module that
  // only exists in the session groups (edge) is appended.
  const allModules = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of modules) seen.set(m.id, m.title);
    for (const g of groups) if (!seen.has(g.moduleId)) seen.set(g.moduleId, g.moduleTitle);
    return [...seen.entries()].map(([id, title]) => ({ id, title }));
  }, [modules, groups]);

  // Group day-groups under EVERY module (including empty ones), in module order.
  const modulesWithDays = useMemo(() => {
    const daysByModule = new Map<string, typeof groups>();
    for (const g of groups) {
      const arr = daysByModule.get(g.moduleId) ?? [];
      arr.push(g);
      daysByModule.set(g.moduleId, arr);
    }
    return allModules
      .filter((m) => moduleFilter === '__all__' || m.id === moduleFilter)
      .map((m) => ({
        moduleId: m.id,
        moduleTitle: m.title,
        days: [...(daysByModule.get(m.id) ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
      }));
  }, [allModules, groups, moduleFilter]);

  const handleSave = async (group: { moduleId: string; date: string }) => {
    const key = `${group.moduleId}::${group.date}`;
    const newDate = draftDates[key];
    if (!newDate || newDate === group.date) return;
    setSavedMsg(null);
    try {
      const res = await updateDay.mutateAsync({
        moduleId: group.moduleId,
        oldDate: group.date,
        newDate,
      });
      setSavedMsg(`${res.moved} sesión(es) movida(s) al ${newDate}.`);
      setDraftDates((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      setSavedMsg(
        e instanceof Error ? `Error: ${e.message}` : 'Error al mover las sesiones del día',
      );
    }
  };

  const formatHeader = (ymd: string): string => {
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return ymd;
    const dt = new Date(y, m - 1, d);
    return formatDate(dt, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Cronograma de clases
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Calendario completo de la sección. Cambiar una fecha mueve todas las sesiones de ese día
            y se conserva al regenerar el syllabus.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FactCheckRounded />}
          onClick={() => setMatrixOpen(true)}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          Matriz de asistencia
        </Button>
      </Stack>

      {allModules.length > 1 && (
        <FormControl size="small" sx={{ maxWidth: 360 }}>
          <InputLabel>Filtrar por módulo</InputLabel>
          <Select
            label="Filtrar por módulo"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
          >
            <MenuItem value="__all__">Todos los módulos</MenuItem>
            {allModules.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isLoading && (
        <Box textAlign="center" py={4}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error && (
        <Alert severity="error">{errorText(error, 'No se pudieron cargar las sesiones')}</Alert>
      )}
      {savedMsg && <Alert severity="info">{savedMsg}</Alert>}

      {!isLoading && modulesWithDays.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary" variant="body2">
            {allModules.length === 0
              ? 'Esta sección aún no tiene módulos. Crea uno con “Nuevo módulo” o genera el syllabus desde la plantilla.'
              : 'Ningún módulo coincide con el filtro.'}
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {modulesWithDays.map((mod) => {
          const canEditModule = modules.some((m) => m.id === mod.moduleId);
          return (
            <Paper
              key={mod.moduleId}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, borderColor: 'rgba(99,102,241,0.35)' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6" fontWeight={800} sx={{ flex: 1, minWidth: 0 }}>
                  {mod.moduleTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {mod.days.length} día(s)
                </Typography>
                {canEditModule && (
                  <Tooltip title="Editar módulo">
                    <IconButton size="small" onClick={() => onEditModule(mod.moduleId)}>
                      <EditRounded fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              <Stack spacing={1}>
                {mod.days.map((g) => {
                  const key = `${g.moduleId}::${g.date}`;
                  const draft = draftDates[key] ?? g.date;
                  const dirty = draft !== g.date;
                  const firstStart = g.sessions[0]?.startTime ?? '';
                  const lastEnd = g.sessions[g.sessions.length - 1]?.endTime ?? '';
                  return (
                    <Paper
                      key={key}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: g.dateOverridden ? 'rgba(245,158,11,0.04)' : 'transparent',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {editingDayKey === key ? (
                              <>
                                <TenantDateField
                                  size="small"
                                  value={draft}
                                  onChange={(v) => setDraftDates((prev) => ({ ...prev, [key]: v }))}
                                />
                                <Tooltip title="Mover el día a esta fecha">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={!dirty || updateDay.isPending}
                                      onClick={async () => {
                                        await handleSave({ moduleId: g.moduleId, date: g.date });
                                        setEditingDayKey(null);
                                      }}
                                    >
                                      <CheckRounded fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Cancelar">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setDraftDates((prev) => {
                                        const next = { ...prev };
                                        delete next[key];
                                        return next;
                                      });
                                      setEditingDayKey(null);
                                    }}
                                  >
                                    <CloseRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" fontWeight={700}>
                                  {formatHeader(g.date)}
                                </Typography>
                                {g.dateOverridden && (
                                  <Chip
                                    size="small"
                                    label="Override"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      bgcolor: 'rgba(245,158,11,0.18)',
                                      color: '#fbbf24',
                                    }}
                                  />
                                )}
                                <Tooltip title="Mover el día a otra fecha">
                                  <IconButton size="small" onClick={() => setEditingDayKey(key)}>
                                    <EditCalendarRounded fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Mover el día a otro módulo">
                                  <IconButton
                                    size="small"
                                    onClick={() => openMoveDay(g.moduleId, g.date)}
                                  >
                                    <DriveFileMoveOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                  sx={{
                                    ml: 0.5,
                                    pl: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'rgba(245,158,11,0.35)',
                                    bgcolor: 'rgba(245,158,11,0.06)',
                                  }}
                                >
                                  <Tooltip title="Modalidad del día — define quién marca (docente si es presencial, alumno si es online)">
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <FactCheckRounded
                                        sx={{ fontSize: 15, color: '#fbbf24', ml: 0.25 }}
                                      />
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#fbbf24', fontWeight: 600 }}
                                      >
                                        Asistencia
                                      </Typography>
                                    </Stack>
                                  </Tooltip>
                                  <Select
                                    size="small"
                                    variant="standard"
                                    value={g.modality}
                                    disableUnderline
                                    onChange={(e) =>
                                      updateModality.mutate({
                                        moduleId: g.moduleId,
                                        date: g.date,
                                        modality: e.target.value as
                                          | 'in-person'
                                          | 'online'
                                          | 'hybrid',
                                      })
                                    }
                                    sx={{
                                      fontSize: 12,
                                      '& .MuiSelect-select': { py: 0, pl: 0.5 },
                                    }}
                                  >
                                    <MenuItem value="in-person" sx={{ fontSize: 13 }}>
                                      Presencial
                                    </MenuItem>
                                    <MenuItem value="online" sx={{ fontSize: 13 }}>
                                      Online
                                    </MenuItem>
                                    <MenuItem value="hybrid" sx={{ fontSize: 13 }}>
                                      Híbrida
                                    </MenuItem>
                                  </Select>
                                  <Tooltip
                                    title={
                                      g.attendanceTaken
                                        ? 'Asistencia tomada — editar'
                                        : 'Tomar asistencia del día'
                                    }
                                  >
                                    <Button
                                      size="small"
                                      variant={g.attendanceTaken ? 'text' : 'contained'}
                                      color={g.attendanceTaken ? 'success' : 'warning'}
                                      onClick={() =>
                                        setAttendanceDay({ moduleId: g.moduleId, date: g.date })
                                      }
                                      sx={{
                                        minWidth: 0,
                                        px: 1,
                                        py: 0.25,
                                        fontSize: 11,
                                        textTransform: 'none',
                                      }}
                                    >
                                      {g.attendanceTaken ? 'Tomada ✓' : 'Tomar lista'}
                                    </Button>
                                  </Tooltip>
                                </Stack>
                              </>
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {g.sessions.length} sesión(es)
                            {firstStart && lastEnd ? ` · ${firstStart} – ${lastEnd}` : ''}
                          </Typography>
                          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            {g.sessions.map((s) => {
                              const isEditing = editingSessionId === s.id;
                              return (
                                <Stack
                                  key={s.id}
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  sx={{ minHeight: 28 }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    component="span"
                                  >
                                    · {s.startTime}–{s.endTime}
                                  </Typography>
                                  {isEditing ? (
                                    <>
                                      <TextField
                                        size="small"
                                        variant="standard"
                                        autoFocus
                                        value={editTopicDraft}
                                        onChange={(e) => setEditTopicDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') void saveEditSession(s.id);
                                          if (e.key === 'Escape') cancelEditSession();
                                        }}
                                        sx={{ flex: 1, minWidth: 120 }}
                                        inputProps={{ style: { fontSize: 12 } }}
                                      />
                                      <Tooltip title="Guardar">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            disabled={
                                              !editTopicDraft.trim() || editSession.isPending
                                            }
                                            onClick={() => void saveEditSession(s.id)}
                                          >
                                            <CheckRounded fontSize="inherit" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Cancelar">
                                        <IconButton size="small" onClick={cancelEditSession}>
                                          <CloseRounded fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        component="span"
                                        sx={{ flex: 1, minWidth: 0 }}
                                      >
                                        {s.topic}
                                      </Typography>
                                      {s.topicOverridden && (
                                        <Chip
                                          size="small"
                                          label="Override"
                                          sx={{
                                            height: 16,
                                            fontSize: 9,
                                            bgcolor: 'rgba(245,158,11,0.18)',
                                            color: '#fbbf24',
                                          }}
                                        />
                                      )}
                                      <Tooltip title="Editar tema">
                                        <IconButton
                                          size="small"
                                          onClick={() => startEditSession(s.id, s.topic)}
                                        >
                                          <EditRounded fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Quitar sesión">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={removeSession.isPending}
                                            onClick={() => void handleRemoveSession(s.id)}
                                          >
                                            <DeleteOutlineRounded fontSize="inherit" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Mover a otro módulo">
                                        <IconButton
                                          size="small"
                                          onClick={() => openMoveDialog(s.id, s.topic, g.moduleId)}
                                        >
                                          <DriveFileMoveOutlined fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Stack>
                              );
                            })}
                            <Box>
                              <Button
                                size="small"
                                startIcon={<AddRounded />}
                                onClick={() => openAddDialog(g.moduleId, g.date)}
                                sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, px: 0.5 }}
                              >
                                Agregar sesión
                              </Button>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
                {mod.days.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Este módulo aún no tiene clases agendadas.
                  </Typography>
                )}
                <Box>
                  <Button
                    size="small"
                    startIcon={<AddRounded />}
                    onClick={() => openAddDialog(mod.moduleId, '')}
                    sx={{ textTransform: 'none' }}
                  >
                    Agregar sesión
                  </Button>
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Dialog open={!!addDialog} onClose={closeAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Agregar sesión</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TenantDateField size="small" value={addDate} onChange={setAddDate} label="Fecha" />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Inicio (HH:MM)"
                size="small"
                fullWidth
                placeholder="09:00"
                value={addStart}
                onChange={(e) => setAddStart(e.target.value)}
              />
              <TextField
                label="Fin (HH:MM)"
                size="small"
                fullWidth
                placeholder="10:30"
                value={addEnd}
                onChange={(e) => setAddEnd(e.target.value)}
              />
            </Stack>
            <TextField
              label="Tema"
              size="small"
              fullWidth
              value={addTopic}
              onChange={(e) => setAddTopic(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!addSessionValid || addSession.isPending}
            onClick={() => void handleAddSession()}
          >
            {addSession.isPending ? '…' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!movingSession} onClose={closeMoveDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Mover “{movingSession?.topic}” a otro módulo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Cambia la sesión de módulo dentro de esta sección. Se conserva al regenerar el
              syllabus.
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Módulo destino</InputLabel>
              <Select
                label="Módulo destino"
                value={targetModuleId}
                onChange={(e) => setTargetModuleId(e.target.value)}
              >
                {modules
                  .filter((m) => m.id !== movingSession?.moduleId)
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!moveValid || moveSession.isPending}
            onClick={() => void handleMoveSession()}
          >
            {moveSession.isPending ? '…' : 'Mover'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!movingDay} onClose={closeMoveDay} maxWidth="xs" fullWidth>
        <DialogTitle>Mover el día a otro módulo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Mueve TODAS las sesiones de {movingDay ? formatHeader(movingDay.date) : ''} a otro
              módulo de esta sección. Se conserva al regenerar el syllabus.
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Módulo destino</InputLabel>
              <Select
                label="Módulo destino"
                value={dayTargetModuleId}
                onChange={(e) => setDayTargetModuleId(e.target.value)}
              >
                {modules
                  .filter((m) => m.id !== movingDay?.moduleId)
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDay}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!moveDayValid || moveDay.isPending}
            onClick={() => void handleMoveDay()}
          >
            {moveDay.isPending ? '…' : 'Mover día'}
          </Button>
        </DialogActions>
      </Dialog>

      {attendanceDay && (
        <AttendanceRosterDialog
          tenantId={tenantId}
          moduleId={attendanceDay.moduleId}
          date={attendanceDay.date}
          onClose={() => setAttendanceDay(null)}
          onSaved={() => {
            setAttendanceDay(null);
            void invalidate();
          }}
        />
      )}

      {matrixOpen && (
        <AttendanceMatrixDialog
          tenantId={tenantId}
          sectionId={sectionId}
          onClose={() => setMatrixOpen(false)}
        />
      )}
    </Stack>
  );
}

// ─── Attendance roster dialog (teacher takes attendance for a session) ────────

// ─── Page editor dialog ──────────────────────────────────────────────────────

function PageEditorDialog({
  mode,
  sectionId,
  moduleId,
  pageId,
  onClose,
  onSuccess,
}: {
  mode: 'create' | 'edit';
  sectionId: string;
  moduleId: string;
  pageId?: string;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const [title, setTitle] = useState('');
  const [bodyJson, setBodyJson] = useState<Record<string, unknown>>({
    type: 'doc',
    content: [{ type: 'paragraph' }],
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'edit' && pageId && currentTenantId) {
      setLoading(true);
      lmsApi
        .getPage({ tenantId: currentTenantId, pageId })
        .then((p) => {
          setTitle(p.title);
          setBodyJson(p.bodyJson);
        })
        .catch((e) => setErr(errorText(e, 'Error al cargar')))
        .finally(() => setLoading(false));
    }
  }, [mode, pageId, currentTenantId]);

  const handleSubmit = async () => {
    if (!currentTenantId) return;
    if (!title.trim()) {
      setErr('Título obligatorio');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      if (mode === 'create') {
        const { id } = await lmsApi.createPage({
          tenantId: currentTenantId,
          sectionId,
          title: title.trim(),
          bodyJson,
        });
        await lmsApi.createModuleItem({
          tenantId: currentTenantId,
          sectionId,
          moduleId,
          type: 'page',
          targetId: id,
        });
      } else if (pageId) {
        await lmsApi.updatePage({
          tenantId: currentTenantId,
          pageId,
          title: title.trim(),
          bodyJson,
        });
      }
      await onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle>{mode === 'create' ? 'Nueva página' : 'Editar página'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {err && <Alert severity="error">{err}</Alert>}
          {loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <TextField
                label="Título"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                inputProps={{ maxLength: 200 }}
                fullWidth
              />
              <PageEditor value={bodyJson} onChange={setBodyJson} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || loading}>
          {submitting ? 'Guardando…' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Material picker dialog ──────────────────────────────────────────────────

function MaterialPickerDialog({
  sectionId,
  moduleId,
  existingTargetIds,
  onClose,
  onSuccess,
}: {
  sectionId: string;
  moduleId: string;
  existingTargetIds: Set<string>;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const { currentTenantId } = useTenantStore();
  const [materials, setMaterials] = useState<Array<{ id: string; title: string; type: string }>>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    courseMaterialApi
      .list({ tenantId: currentTenantId, sectionId })
      .then((res) => {
        setMaterials(res.items.map((m) => ({ id: m.id, title: m.title, type: m.type })));
      })
      .catch((e: unknown) => setErr(errorText(e)))
      .finally(() => setLoading(false));
  }, [currentTenantId, sectionId]);

  const handleAdd = async (mid: string) => {
    if (!currentTenantId) return;
    setAdding(mid);
    try {
      await lmsApi.createModuleItem({
        tenantId: currentTenantId,
        sectionId,
        moduleId,
        type: 'material',
        targetId: mid,
      });
      await onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e));
    } finally {
      setAdding(null);
    }
  };

  const [search, setSearch] = useState('');
  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return materials.filter((m) => {
      if (existingTargetIds.has(m.id)) return false;
      if (!q) return true;
      return m.title.toLowerCase().includes(q) || m.type.toLowerCase().includes(q);
    });
  }, [materials, existingTargetIds, search]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Buscar en catálogo</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por título o tipo (video, apuntes, lectura)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 0.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={20} />
          </Box>
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : available.length === 0 ? (
          <Typography color="text.secondary">
            {search.trim()
              ? 'Sin coincidencias para esa búsqueda.'
              : 'No hay materiales disponibles en esta sección. Sube uno desde el menú "+ Item" → "Subir archivo" o "Agregar enlace".'}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {available.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Chip label={m.type} size="small" sx={{ mr: 1, height: 20 }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {m.title}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={adding === m.id}
                  onClick={() => handleAdd(m.id)}
                >
                  {adding === m.id ? '…' : 'Vincular'}
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// Pick an existing section assignment and place it in a module as an item.
// Creating a brand-new assignment happens in the Asignaciones tab (which also
// offers a "Módulo" select). A target may be placed in several modules.
function AssignmentPickerDialog({
  sectionId,
  moduleId,
  existingTargetIds,
  onClose,
  onSuccess,
}: {
  sectionId: string;
  moduleId: string;
  existingTargetIds: Set<string>;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [assignments, setAssignments] = useState<
    Array<{ id: string; name: string; type: string; dueAt?: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!currentTenantId) return;
    setLoading(true);
    assignmentApi
      .list({ tenantId: currentTenantId, sectionId })
      .then((res) => {
        setAssignments(
          res.items.map((a) => ({
            id: a.id,
            name: a.name,
            type: a.type,
            ...(a.dueAt ? { dueAt: a.dueAt } : {}),
          })),
        );
      })
      .catch((e: unknown) => setErr(errorText(e)))
      .finally(() => setLoading(false));
  }, [currentTenantId, sectionId]);

  const handleAdd = async (aid: string) => {
    if (!currentTenantId) return;
    setAdding(aid);
    try {
      await lmsApi.createModuleItem({
        tenantId: currentTenantId,
        sectionId,
        moduleId,
        type: 'assignment',
        targetId: aid,
      });
      await onSuccess();
    } catch (e: unknown) {
      setErr(errorText(e));
    } finally {
      setAdding(null);
    }
  };

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (existingTargetIds.has(a.id)) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q) || a.type.toLowerCase().includes(q);
    });
  }, [assignments, existingTargetIds, search]);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('lms.assignmentPicker.title')}</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder={t('lms.assignmentPicker.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 0.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={20} />
          </Box>
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : available.length === 0 ? (
          <Typography color="text.secondary">
            {search.trim() ? t('lms.assignmentPicker.noMatches') : t('lms.assignmentPicker.empty')}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {available.map((a) => (
              <Box
                key={a.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Chip
                  label={t(`assignmentsAdmin.types.${a.type}`, { defaultValue: a.type })}
                  size="small"
                  sx={{ mr: 1, height: 20 }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {a.name}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={adding === a.id}
                  onClick={() => handleAdd(a.id)}
                >
                  {adding === a.id ? '…' : 'Vincular'}
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
